import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowUp, ArrowDown, Upload, Image as ImageIcon, Trophy, Users, Map as MapIcon, ScrollText, X, Pencil, Check } from "lucide-react";
import { api } from "../../lib/api";
import { chunkedUpload, resolveUploadUrl } from "../../lib/upload";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalTextarea, BrutalLabel, Badge, SectionTitle,
} from "../../components/common/Brutal";

const IMG_MAX_MB = 5;

export default function AdminCompetitions() {
  const [list, setList] = useState([]);
  const [activeSlug, setActiveSlug] = useState(null);
  const [comp, setComp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploads, setUploads] = useState({}); // {key: {active, pct, phase}}
  const [newParticipant, setNewParticipant] = useState({ name: "", contestant_number: "", address: "", details: "", photo_url: "" });
  const [participantUp, setParticipantUp] = useState({ active: false, pct: 0, phase: "" });
  const [editingIdx, setEditingIdx] = useState(null); // index of contestant being edited
  const [editDraft, setEditDraft] = useState({ name: "", contestant_number: "", address: "", details: "", photo_url: "" });
  const [editUp, setEditUp] = useState({ active: false, pct: 0, phase: "" });

  const load = async () => {
    const { data } = await api.get("/competitions");
    setList(data);
    if (!activeSlug && data[0]) setActiveSlug(data[0].slug);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!activeSlug) { setComp(null); return; }
    const c = list.find((x) => x.slug === activeSlug);
    setComp(c ? { ...c } : null);
  }, [activeSlug, list]);

  const setUp = (key, state) => setUploads((u) => ({ ...u, [key]: state }));

  const handleFile = async (e, key, onSet) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > IMG_MAX_MB * 1024 * 1024) {
      toast.error(`Max ${IMG_MAX_MB}MB`);
      e.target.value = "";
      return;
    }
    onSet({ active: true, pct: 0, phase: "Starting…" });
    try {
      const res = await chunkedUpload(file, (pct, phase) => onSet({ active: true, pct, phase }));
      onSet({ active: false, pct: 1, phase: "Done" });
      e.target.value = "";
      return res.url_path;
    } catch (err) {
      onSet({ active: false, pct: 0, phase: "" });
      toast.error(err.message || "Upload failed");
      e.target.value = "";
      return null;
    }
  };

  const uploadField = async (e, key) => {
    const url = await handleFile(e, key, (s) => setUp(key, s));
    if (url) setComp((c) => ({ ...c, [key]: url }));
  };

  const uploadGallery = async (e) => {
    const url = await handleFile(e, "gallery", (s) => setUp("gallery", s));
    if (url) setComp((c) => ({ ...c, gallery: [...(c.gallery || []), url] }));
  };

  const moveGallery = (i, dir) => {
    setComp((c) => {
      const arr = [...(c.gallery || [])];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return c;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...c, gallery: arr };
    });
  };
  const removeGallery = (i) => setComp((c) => ({ ...c, gallery: c.gallery.filter((_, idx) => idx !== i) }));

  const uploadParticipantPhoto = async (e) => {
    const url = await handleFile(e, "participant", (s) => setParticipantUp(s));
    if (url) setNewParticipant((p) => ({ ...p, photo_url: url }));
  };
  const addParticipant = () => {
    if (!newParticipant.name) return toast.error("Participant name required");
    setComp((c) => ({ ...c, participants: [...(c.participants || []), { ...newParticipant }] }));
    setNewParticipant({ name: "", contestant_number: "", address: "", details: "", photo_url: "" });
    setParticipantUp({ active: false, pct: 0, phase: "" });
  };
  const removeParticipant = (i) => {
    if (editingIdx === i) { setEditingIdx(null); }
    setComp((c) => ({ ...c, participants: c.participants.filter((_, idx) => idx !== i) }));
  };

  const startEditParticipant = (i) => {
    const p = (comp.participants || [])[i] || {};
    setEditDraft({
      name: p.name || "",
      contestant_number: p.contestant_number || "",
      address: p.address || "",
      details: p.details || "",
      photo_url: p.photo_url || "",
    });
    setEditUp({ active: false, pct: 0, phase: "" });
    setEditingIdx(i);
  };

  const cancelEditParticipant = () => {
    setEditingIdx(null);
    setEditDraft({ name: "", contestant_number: "", address: "", details: "", photo_url: "" });
    setEditUp({ active: false, pct: 0, phase: "" });
  };

  const saveEditParticipant = () => {
    if (!editDraft.name) return toast.error("Contestant name required");
    if (editUp.active) return toast.error("Wait for upload to finish");
    setComp((c) => {
      const arr = [...(c.participants || [])];
      arr[editingIdx] = { ...editDraft };
      return { ...c, participants: arr };
    });
    cancelEditParticipant();
    toast.success("Contestant updated (remember to Save Competition)");
  };

  const uploadEditPhoto = async (e) => {
    const url = await handleFile(e, "edit-photo", (s) => setEditUp(s));
    if (url) setEditDraft((d) => ({ ...d, photo_url: url }));
  };

  const save = async () => {
    if (!comp) return;
    if (Object.values(uploads).some((u) => u && u.active) || participantUp.active) {
      return toast.error("Wait for upload to finish");
    }
    setSaving(true);
    try {
      const payload = {
        introduction: comp.introduction || "",
        banner_url: comp.banner_url || "",
        mechanics_url: comp.mechanics_url || "",
        gallery: comp.gallery || [],
      };
      if (comp.type === "sport") {
        if (comp.slug === "tour-de-kaogma") {
          payload.route_map_road_bike_url = comp.route_map_road_bike_url || "";
          payload.route_map_mtb_url = comp.route_map_mtb_url || "";
        } else {
          payload.route_map_url = comp.route_map_url || "";
        }
      } else payload.participants = comp.participants || [];
      await api.put(`/competitions/${comp.slug}`, payload);
      toast.success("Saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-competitions">
      <SectionTitle kicker="Festival Showdowns" title="Competitions" icon={Trophy} />

      <div className="grid md:grid-cols-[260px_1fr] gap-4">
        <BrutalCard className="p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B4E9E] px-1 pb-2">Competitions</div>
          <ul className="space-y-1">
            {list.map((c) => {
              const on = c.slug === activeSlug;
              return (
                <li key={c.slug}>
                  <button
                    onClick={() => setActiveSlug(c.slug)}
                    className={`w-full text-left kf-border px-2.5 py-2 transition-colors ${on ? "bg-[#3D1E6D] text-white" : "bg-white hover:bg-[#FFB400]"}`}
                    data-testid={`comp-nav-${c.slug}`}
                  >
                    <div className="text-xs font-bold uppercase tracking-widest">{c.label}</div>
                    <div className={`text-[10px] mt-0.5 ${on ? "text-[#FFB400]" : "text-[#6B4E9E]"}`}>
                      <Badge color={on ? "yellow" : c.type === "sport" ? "magenta" : "purple"}>{c.type}</Badge>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </BrutalCard>

        {comp && (
          <BrutalCard className="p-5 space-y-5" data-testid={`comp-form-${comp.slug}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <Badge color="magenta">{comp.type === "sport" ? "Sport" : "Performance"}</Badge>
                <h3 className="display text-3xl mt-2">{comp.label}</h3>
                <div className="text-xs uppercase tracking-widest text-[#6B4E9E]">{comp.date} · {comp.venue}</div>
              </div>
              <BrutalButton variant="primary" onClick={save} disabled={saving} data-testid="comp-save">
                {saving ? "Saving…" : (<><Save className="h-4 w-4" /> Save</>)}
              </BrutalButton>
            </div>

            <div>
              <BrutalLabel>Competition Introduction</BrutalLabel>
              <BrutalTextarea
                value={comp.introduction || ""}
                onChange={(e) => setComp({ ...comp, introduction: e.target.value })}
                placeholder="Describe the competition, format, prizes, who can join…"
                data-testid="comp-intro"
                rows={5}
              />
            </div>

            <UploadField
              label="Competition Banner"
              icon={ImageIcon}
              url={comp.banner_url}
              up={uploads.banner_url}
              onPick={(e) => uploadField(e, "banner_url")}
              testid="comp-banner"
              onClear={() => setComp({ ...comp, banner_url: "" })}
            />

            <UploadField
              label="Mechanics"
              icon={ScrollText}
              url={comp.mechanics_url}
              up={uploads.mechanics_url}
              onPick={(e) => uploadField(e, "mechanics_url")}
              testid="comp-mechanics"
              onClear={() => setComp({ ...comp, mechanics_url: "" })}
            />

            {comp.type === "sport" && comp.slug !== "tour-de-kaogma" && (
              <UploadField
                label="Route Map"
                icon={MapIcon}
                url={comp.route_map_url}
                up={uploads.route_map_url}
                onPick={(e) => uploadField(e, "route_map_url")}
                testid="comp-route-map"
                onClear={() => setComp({ ...comp, route_map_url: "" })}
              />
            )}

            {comp.slug === "tour-de-kaogma" && (
              <>
                <UploadField
                  label="Route Map — 73 km Road Bike"
                  icon={MapIcon}
                  url={comp.route_map_road_bike_url}
                  up={uploads.route_map_road_bike_url}
                  onPick={(e) => uploadField(e, "route_map_road_bike_url")}
                  testid="comp-route-map-road-bike"
                  onClear={() => setComp({ ...comp, route_map_road_bike_url: "" })}
                />
                <UploadField
                  label="Route Map — 78 km MTB"
                  icon={MapIcon}
                  url={comp.route_map_mtb_url}
                  up={uploads.route_map_mtb_url}
                  onPick={(e) => uploadField(e, "route_map_mtb_url")}
                  testid="comp-route-map-mtb"
                  onClear={() => setComp({ ...comp, route_map_mtb_url: "" })}
                />
              </>
            )}

            {/* GALLERY */}
            <div className="kf-border p-4 bg-[#FFF4E6]">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2"><ImageIcon className="h-4 w-4 text-[#E5247C]" /><div className="text-xs font-bold uppercase tracking-widest">Gallery ({(comp.gallery || []).length})</div></div>
                <label className="inline-flex items-center kf-border bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-widest cursor-pointer hover:bg-[#FFB400]">
                  <Upload className="h-3 w-3 mr-1.5" /> Add Photo
                  <input type="file" accept="image/*" className="hidden" onChange={uploadGallery} data-testid="comp-gallery-upload" />
                </label>
              </div>
              <UploadProgress state={uploads.gallery} />
              {(comp.gallery || []).length === 0 && <p className="text-sm text-[#6B4E9E]">No photos yet.</p>}
              <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {(comp.gallery || []).map((g, i) => (
                  <div key={i} className="kf-border bg-white" data-testid={`comp-gallery-${i}`}>
                    <img src={resolveUploadUrl(g)} alt={`gallery ${i}`} loading="lazy" className="w-full h-32 object-cover" />
                    <div className="p-1.5 flex items-center justify-between gap-1">
                      <span className="text-[10px] font-bold text-[#6B4E9E]">#{i + 1}</span>
                      <div className="flex gap-1">
                        <button onClick={() => moveGallery(i, -1)} disabled={i === 0} className="kf-border bg-white p-1 disabled:opacity-30" title="Move up" data-testid={`comp-gallery-up-${i}`}><ArrowUp className="h-3 w-3" /></button>
                        <button onClick={() => moveGallery(i, 1)} disabled={i === comp.gallery.length - 1} className="kf-border bg-white p-1 disabled:opacity-30" title="Move down" data-testid={`comp-gallery-down-${i}`}><ArrowDown className="h-3 w-3" /></button>
                        <button onClick={() => removeGallery(i)} className="kf-border bg-[#EF4444] text-white p-1" title="Remove" data-testid={`comp-gallery-del-${i}`}><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CONTESTANTS (performance only) */}
            {comp.type === "performance" && (
              <div className="kf-border p-4 bg-[#FFF4E6]">
                <div className="flex items-center gap-2 mb-3"><Users className="h-4 w-4 text-[#E5247C]" /><div className="text-xs font-bold uppercase tracking-widest">Contestants ({(comp.participants || []).length})</div></div>

                <div className="grid sm:grid-cols-2 gap-2 mb-3">
                  <BrutalInput placeholder="Contestant # (e.g. 01)" value={newParticipant.contestant_number} onChange={(e) => setNewParticipant({...newParticipant, contestant_number: e.target.value})} data-testid="participant-number" />
                  <BrutalInput placeholder="Name" value={newParticipant.name} onChange={(e) => setNewParticipant({...newParticipant, name: e.target.value})} data-testid="participant-name" />
                </div>
                <BrutalInput className="mb-3" placeholder="Address (e.g. Naga City, Camarines Sur)" value={newParticipant.address} onChange={(e) => setNewParticipant({...newParticipant, address: e.target.value})} data-testid="participant-address" />
                <BrutalTextarea placeholder="Details (bio, hometown, sponsor…)" value={newParticipant.details} onChange={(e) => setNewParticipant({...newParticipant, details: e.target.value})} data-testid="participant-details" rows={3} />
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <label className="inline-flex items-center kf-border bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest cursor-pointer hover:bg-[#FFB400]">
                    <Upload className="h-3 w-3 mr-1.5" /> {newParticipant.photo_url ? "Replace Photo" : "Upload Photo"}
                    <input type="file" accept="image/*" className="hidden" onChange={uploadParticipantPhoto} data-testid="participant-photo" />
                  </label>
                  {newParticipant.photo_url && <img src={resolveUploadUrl(newParticipant.photo_url)} alt="preview" className="h-10 w-10 object-cover kf-border" />}
                  <BrutalButton variant="primary" onClick={addParticipant} type="button" data-testid="participant-add">
                    <Plus className="h-4 w-4" /> Add Contestant
                  </BrutalButton>
                </div>
                <UploadProgress state={participantUp} />

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                  {(comp.participants || []).map((p, i) =>
                    editingIdx === i ? (
                      <div key={i} className="kf-border bg-white p-3 sm:col-span-2 lg:col-span-3" data-testid={`participant-edit-${i}`}>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[#E5247C] mb-2">Editing contestant #{i + 1}</div>
                        <div className="grid sm:grid-cols-2 gap-2">
                          <BrutalInput placeholder="Contestant #" value={editDraft.contestant_number} onChange={(e) => setEditDraft({...editDraft, contestant_number: e.target.value})} data-testid={`participant-edit-number-${i}`} />
                          <BrutalInput placeholder="Name" value={editDraft.name} onChange={(e) => setEditDraft({...editDraft, name: e.target.value})} data-testid={`participant-edit-name-${i}`} />
                        </div>
                        <BrutalInput className="mt-2" placeholder="Address" value={editDraft.address} onChange={(e) => setEditDraft({...editDraft, address: e.target.value})} data-testid={`participant-edit-address-${i}`} />
                        <BrutalTextarea className="mt-2" placeholder="Details" value={editDraft.details} onChange={(e) => setEditDraft({...editDraft, details: e.target.value})} data-testid={`participant-edit-details-${i}`} rows={3} />
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                          <label className="inline-flex items-center kf-border bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest cursor-pointer hover:bg-[#FFB400]">
                            <Upload className="h-3 w-3 mr-1.5" /> {editDraft.photo_url ? "Replace Photo" : "Upload Photo"}
                            <input type="file" accept="image/*" className="hidden" onChange={uploadEditPhoto} data-testid={`participant-edit-photo-${i}`} />
                          </label>
                          {editDraft.photo_url && <img src={resolveUploadUrl(editDraft.photo_url)} alt="preview" className="h-10 w-10 object-cover kf-border" />}
                        </div>
                        <UploadProgress state={editUp} />
                        <div className="mt-3 flex gap-2">
                          <BrutalButton variant="primary" onClick={saveEditParticipant} disabled={editUp.active} data-testid={`participant-edit-save-${i}`}>
                            <Check className="h-4 w-4" /> Save Changes
                          </BrutalButton>
                          <BrutalButton variant="outline" onClick={cancelEditParticipant}>
                            <X className="h-4 w-4" /> Cancel
                          </BrutalButton>
                        </div>
                      </div>
                    ) : (
                      <div key={i} className="kf-border bg-white p-3" data-testid={`participant-${i}`}>
                        <div className="flex items-start gap-3">
                          {p.photo_url ? (
                            <img src={resolveUploadUrl(p.photo_url)} alt={p.name} loading="lazy" className="h-16 w-16 object-cover kf-border shrink-0" />
                          ) : (
                            <div className="h-16 w-16 kf-border bg-[#FFB400] grid place-items-center text-[#3D1E6D] shrink-0 text-xs font-bold">N/A</div>
                          )}
                          <div className="flex-1 min-w-0">
                            {p.contestant_number && <div className="display text-2xl text-[#E5247C] font-bold leading-none">#{p.contestant_number}</div>}
                            <div className="display text-lg text-[#3D1E6D] leading-tight mt-1">{p.name}</div>
                            {p.address && <div className="text-[10px] uppercase tracking-widest text-[#6B4E9E] mt-0.5">{p.address}</div>}
                            {p.details && <p className="text-xs text-[#6B4E9E] mt-1 line-clamp-3">{p.details}</p>}
                          </div>
                          <div className="flex flex-col gap-1 shrink-0">
                            <button onClick={() => startEditParticipant(i)} className="kf-border bg-white p-1 hover:bg-[#FFB400]" title="Edit" data-testid={`participant-edit-btn-${i}`}><Pencil className="h-3 w-3" /></button>
                            <button onClick={() => removeParticipant(i)} className="kf-border bg-[#EF4444] text-white p-1" title="Delete" data-testid={`participant-del-${i}`}><Trash2 className="h-3 w-3" /></button>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <BrutalButton variant="primary" onClick={save} disabled={saving} data-testid="comp-save-bottom">
                {saving ? "Saving…" : (<><Save className="h-4 w-4" /> Save Competition</>)}
              </BrutalButton>
            </div>
          </BrutalCard>
        )}
      </div>
    </div>
  );
}

const UploadField = ({ label, icon: Icon, url, up, onPick, onClear, testid }) => (
  <div className="kf-border p-4 bg-[#FFF4E6]">
    <div className="flex items-center gap-2 mb-3"><Icon className="h-4 w-4 text-[#E5247C]" /><div className="text-xs font-bold uppercase tracking-widest">{label}</div></div>
    <div className="flex items-center gap-3 flex-wrap">
      <label className="inline-flex items-center kf-border bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest cursor-pointer hover:bg-[#FFB400]">
        <Upload className="h-3 w-3 mr-1.5" /> {url ? "Replace" : "Upload"}
        <input type="file" accept="image/*" className="hidden" onChange={onPick} data-testid={`${testid}-input`} />
      </label>
      {url && (
        <>
          <img src={resolveUploadUrl(url)} alt={label} className="h-16 w-auto max-w-[160px] object-cover kf-border" />
          <button onClick={onClear} className="kf-border bg-[#EF4444] text-white p-1.5" data-testid={`${testid}-clear`}><X className="h-3 w-3" /></button>
        </>
      )}
    </div>
    <UploadProgress state={up} />
  </div>
);

const UploadProgress = ({ state }) => {
  if (!state || (!state.active && (state.pct || 0) === 0)) return null;
  const pct = Math.round((state.pct || 0) * 100);
  const done = !state.active && state.pct >= 1;
  return (
    <div className="mt-3" data-pct={pct}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-widest text-[#3D1E6D]">
          {done ? "Upload complete" : (state.phase || "Working…")}
        </span>
        <span className="text-[10px] font-bold tabular-nums text-[#E5247C]">{pct}%</span>
      </div>
      <div className="h-2 w-full kf-border bg-white overflow-hidden">
        <div className={`h-full transition-all duration-150 ${done ? "bg-[#22C55E]" : "bg-[#E5247C]"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};
